import { el, firstNonEmpty, rawFallback, stringify } from "./dom.js"
import { t, titleForKey } from "./i18n.js"
import { renderCompositeBlocks } from "./blocks/composite.js"
import { renderMarkdownHtml, getPurify } from "./markdown.js"

function sectionData(section) {
  return section?.data && typeof section.data === "object" ? section.data : {}
}

function present(value) {
  return value != null && value !== ""
}

function sameValue(a, b) {
  if (typeof a === "number" && typeof b === "number") return a === b
  return String(a) === String(b)
}

function isScaleMap(value) {
  return value && typeof value === "object" && !Array.isArray(value)
}

function scaleSource(data) {
  if (isScaleMap(data.by_scale) && Object.keys(data.by_scale).length) return data.by_scale
  if (isScaleMap(data.scales) && Object.keys(data.scales).length) return data.scales
  return null
}

function orderedCodes(data, source) {
  const order = Array.isArray(data.scale_order) ? data.scale_order.map(String) : []
  const rest = Object.keys(source).filter((code) => !order.includes(code))
  return order.length ? [...order, ...rest] : Object.keys(source)
}

function resultOf(row) {
  if (present(row.display)) return row.display
  if (present(row.translation)) return row.translation
  if (present(row.value)) return row.value
  return row.score
}

function normalizeScaleRow(code, rawRow) {
  const row = rawRow && typeof rawRow === "object" ? rawRow : { score: rawRow }
  const result = resultOf(row)
  const raw = row.raw_score
  return {
    code: code ?? row.code,
    label: firstNonEmpty(row.scale_label, row.label, row.name, row.scale_code, code, row.code),
    result,
    raw: present(raw) && !sameValue(raw, result) ? raw : null,
    level: firstNonEmpty(row.zone_label, row.zone, row.level),
    hint: row.zone_hint || "",
    threshold: row.threshold_label || "",
    zone: row.zone || row.level || "",
    deviation: row.deviation,
    clientVisible: row.client_visible,
  }
}

function collectScaleRows(data) {
  const source = scaleSource(data)
  if (source) {
    return orderedCodes(data, source).flatMap((code) => {
      if (!Object.prototype.hasOwnProperty.call(source, code) || source[code] == null) return []
      return [normalizeScaleRow(code, source[code])]
    })
  }
  const rows = []
  if (Array.isArray(data.scales)) {
    for (const row of data.scales) rows.push(normalizeScaleRow(row?.code, row))
  }
  if (Array.isArray(data.rows)) {
    for (const row of data.rows) rows.push(normalizeScaleRow(row?.code, row))
  }
  return rows
}

function zoneClass(zone) {
  const key = String(zone || "norm")
  return /^[\w-]+$/.test(key) ? key : "norm"
}

function appendLeading(root, data, locale) {
  const label = data.leading?.label
  if (label) {
    const line = el("p", { className: "report-leading" })
    line.append(el("strong", {}, t("unified_report.leading.title", { locale })))
    line.append(document.createTextNode(` ${label}`))
    root.append(line)
  }
  const shares = isScaleMap(data.shares) ? data.shares : null
  if (!shares || !Object.keys(shares).length) return
  const source = scaleSource(data) || {}
  const ul = el("ul", { className: "report-list report-shares" })
  for (const code of orderedCodes(data, shares)) {
    if (!present(shares[code])) continue
    const name = firstNonEmpty(source[code]?.scale_label, source[code]?.label, code)
    ul.append(el("li", {}, t("unified_report.leading.share", { locale, label: name, value: shares[code] })))
  }
  if (ul.childNodes.length) root.append(ul)
}

function scaleTable(rows, locale) {
  const showRaw = rows.some((row) => row.raw != null)
  const showDeviation = rows.some((row) => row.deviation != null)
  const head = el("tr", {}, el("th", {}, t("tests.result.scale_name", { locale })))
  if (showRaw) head.append(el("th", {}, t("tests.result.raw_score", { locale })))
  head.append(
    el("th", {}, t(showRaw ? "tests.result.result_value" : "tests.result.score", { locale })),
    el("th", {}, t("tests.result.analysis.zone_column", { locale }))
  )
  if (showDeviation) head.append(el("th", {}, t("tests.result.deviation", { locale })))

  const tbody = el("tbody")
  for (const row of rows) {
    const labelCell = el("td")
    labelCell.append(el("strong", {}, row.label || ""))
    if (row.code && row.code !== row.label) {
      labelCell.append(document.createTextNode(" "))
      labelCell.append(el("span", { className: "muted" }, `(${row.code})`))
    }
    const zoneCell = el("td")
    zoneCell.append(el("span", { className: `zone zone--${zoneClass(row.zone)}` }, row.level || ""))
    if (row.hint) zoneCell.append(el("p", { className: "muted" }, row.hint))
    if (row.threshold) zoneCell.append(el("p", { className: "muted" }, row.threshold))
    const cells = [labelCell]
    if (showRaw) cells.push(el("td", {}, row.raw == null ? "" : stringify(row.raw)))
    cells.push(el("td", {}, stringify(row.result)), zoneCell)
    if (showDeviation) cells.push(el("td", {}, row.deviation == null ? "" : stringify(row.deviation)))
    tbody.append(el("tr", {}, ...cells))
  }
  const table = el("table", { className: "report-table" })
  table.append(el("thead", {}, head), tbody)
  return table
}

export function renderScaleAnalysis(section, opts = {}) {
  const locale = opts.locale || "ru"
  const data = sectionData(section)
  const collected = collectScaleRows(data)
  const rows =
    opts.audience === "respondent" ? collected.filter((row) => row.clientVisible !== false) : collected
  const root = el("div")
  appendLeading(root, data, locale)
  if (!rows.length) {
    if (root.childNodes.length) return root
    return collected.length ? el("div") : rawFallback(data)
  }
  root.append(scaleTable(rows, locale))
  return root
}

export function renderCombinationRules(section) {
  const data = sectionData(section)
  const items = []
  for (const raw of data.items || []) {
    if (typeof raw === "string") {
      items.push(raw)
      continue
    }
    if (raw && typeof raw === "object") {
      items.push(
        firstNonEmpty(raw.interpretation, raw.text, raw.body, raw.description, raw.message, JSON.stringify(raw))
      )
    }
  }
  if (!items.length) return rawFallback(data)
  const ul = el("ul", { className: "report-list" })
  for (const text of items) ul.append(el("li", {}, text))
  return ul
}

export function renderContradictions(section, { locale }) {
  const data = sectionData(section)
  const items = data.items || []
  if (!items.length) return rawFallback(data)
  const ul = el("ul", { className: "report-list" })
  for (const it of items) {
    const text = firstNonEmpty(
      it.description,
      it.text,
      t(it.description_key, { locale }),
      it.description_key
    )
    const li = el("li")
    li.append(
      el("strong", {}, `${firstNonEmpty(it.scale_a_label, it.scale_a)} ↔ ${firstNonEmpty(it.scale_b_label, it.scale_b)}`)
    )
    const level = firstNonEmpty(it.level_label, it.level)
    if (level) {
      li.append(document.createTextNode(" "))
      li.append(el("span", { className: "tag" }, level))
    }
    if (text) li.append(document.createTextNode(` — ${text}`))
    ul.append(li)
  }
  return ul
}

export function renderNotification(section, { locale }) {
  const data = sectionData(section)
  const level = data.level || data.kind || "info"
  let message = firstNonEmpty(data.message, data.text, data.body)
  if (!message && data.body_key) message = t(data.body_key, { locale })
  if (!message && section.title_key) message = t(section.title_key, { locale })
  const root =
    level === "muted"
      ? el("div", { className: "hint" })
      : el("div", { className: `notification notification--${level}`, role: level === "danger" ? "alert" : "status" })
  root.append(el("p", {}, message || ""))
  if (Array.isArray(data.items) && data.items.length) {
    const ul = el("ul", { className: "report-list" })
    for (const item of data.items) ul.append(el("li", {}, stringify(item)))
    root.append(ul)
  }
  return root
}

export function renderInterpretationList(section, opts = {}) {
  const locale = opts.locale || "ru"
  const data = sectionData(section)
  const root = el("div")
  if (data.code_type) {
    root.append(el("p", {}, stringify(data.code_type)))
    if (data.code_type_key) {
      const translated = t(data.code_type_key, { locale })
      root.append(
        el(
          "p",
          { className: translated ? "" : "hint" },
          translated || t("mmpi.result.code_type_template_placeholder", { locale })
        )
      )
    }
  }
  const source = data.items || data.lines || []
  const items =
    opts.audience === "respondent" ? source.filter((item) => item?.client_visible !== false) : source
  if (!items.length && !data.code_type) return source.length ? el("div") : rawFallback(data)
  if (items.length) {
    const ul = el("ul", { className: "report-list" })
    for (const item of items) {
      if (typeof item === "string") {
        ul.append(el("li", {}, item))
        continue
      }
      if (item?.kind === "i18n_key" || item?.key) {
        ul.append(el("li", {}, t(item.key, { locale, defaultValue: item.text || item.key })))
        continue
      }
      ul.append(el("li", {}, firstNonEmpty(item.text, item.body, JSON.stringify(item))))
    }
    root.append(ul)
  }
  return root
}

function flagText(flag, { locale, instrumentCode }) {
  const specific = instrumentCode ? t(`unified_report.flags.${instrumentCode}.${flag}`, { locale }) : ""
  if (specific) return specific
  const generic = t(`unified_report.flags.${flag}`, { locale })
  if (generic) return generic
  return t("unified_report.flags.fallback", { locale })
}

function validityStatusClass(status) {
  if (status === "unreliable") return "tag--danger"
  if (status === "reliable") return "tag--ok"
  return "tag--muted"
}

export function renderValidity(section, opts = {}) {
  const locale = opts.locale || "ru"
  const data = sectionData(section)
  const root = el("div", { className: "mb-4" })
  if (data.incomplete) {
    root.append(
      el(
        "div",
        { className: "notification notification--warning" },
        el("p", {}, titleForKey(section.title_key, locale) || section.title_key || "")
      )
    )
    return root
  }
  if (data.verdict) {
    const verdictClass =
      data.verdict === "invalid" ? "tag--danger" : data.verdict === "caution" ? "tag--muted" : "tag--ok"
    root.append(
      el("p", {}, el("span", { className: `tag ${verdictClass}` }, t(`mmpi.result.verdict.${data.verdict}`, {
        locale,
        defaultValue: data.verdict,
      })))
    )
    if (Array.isArray(data.flags) && data.flags.length) {
      const ul = el("ul", { className: "report-list" })
      for (const flag of data.flags) {
        ul.append(el("li", {}, t(`mmpi.result.flags.${flag}`, { locale, defaultValue: String(flag) })))
      }
      root.append(ul)
    }
    if (Number(data.cannot_say_count) > 0) {
      root.append(
        el("p", { className: "hint" }, t("mmpi.result.cannot_say_count", { locale, count: data.cannot_say_count }))
      )
    }
    return root
  }

  const status = data.status ? String(data.status) : ""
  const flags = Array.isArray(data.flags) ? data.flags.map(String).filter(Boolean) : []
  const hasIndex = data.index != null && data.index !== ""
  if (!status && !flags.length && !hasIndex) return rawFallback(data)

  if (status) {
    const label = t(`unified_report.validity.status.${status}`, { locale, defaultValue: status })
    root.append(el("p", {}, el("span", { className: `tag ${validityStatusClass(status)}` }, label)))
  }
  if (hasIndex) {
    root.append(el("p", {}, t("unified_report.validity.index", { locale, value: data.index })))
  }
  const extra = flags.filter((flag) => flag !== status)
  if (extra.length) {
    const ul = el("ul", { className: "report-list" })
    for (const flag of extra) {
      ul.append(el("li", {}, flagText(flag, { locale, instrumentCode: opts.instrumentCode })))
    }
    root.append(ul)
  }
  return root
}

export function renderProfileChart(section, { locale }) {
  const data = sectionData(section)
  const root = el("div")
  if (data.svg) {
    const figure = el("figure", { className: "report-chart-scroll" })
    const box = el("div")
    const clean = getPurify().sanitize(String(data.svg), {
      USE_PROFILES: { svg: true, svgFilters: true },
      KEEP_CONTENT: false,
    })
    box.innerHTML = clean
    figure.append(box)
    root.append(figure)
  } else {
    root.append(el("p", { className: "hint" }, t("unified_report.profile_chart.cabinet_hint", { locale })))
  }
  const scales = data.scales || []
  if (scales.length) {
    const table = el("table", { className: "report-table" })
    table.append(
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          el("th", {}, t("mmpi.result.table.scale", { locale })),
          el("th", {}, t("mmpi.result.table.t_score", { locale }))
        )
      )
    )
    const tbody = el("tbody")
    const order = data.scale_order?.length ? data.scale_order : scales.map((s) => s.code)
    const byCode = Object.fromEntries(scales.map((s) => [s.code, s]))
    for (const code of order) {
      const s = byCode[code] || scales.find((row) => row.code === code)
      if (!s) continue
      tbody.append(
        el(
          "tr",
          {},
          el("td", {}, firstNonEmpty(t(`mmpi.scale_axis.${code}`, { locale }), s.code)),
          el("td", {}, stringify(s.value ?? s.t_score ?? s.score))
        )
      )
    }
    table.append(tbody)
    root.append(table)
  } else if (!data.svg) {
    return rawFallback(data)
  }
  return root
}

function mapSwatchRows(rows) {
  return (rows || []).map((row, i) => ({
    position: row.position ?? i + 1,
    id: firstNonEmpty(stringify(row.color_code), stringify(row.id)),
    hex: stringify(row.hex),
    label: firstNonEmpty(stringify(row.color_label), stringify(row.color_code), stringify(row.id)),
    onDark: !!row.on_dark,
    interpretation: stringify(row.interpretation),
  }))
}

function renderInterpBlock(view, { locale }) {
  if (!view) return null
  const block = el("div", { className: "interpretation-blocks" })
  const title = view.title || t("tests.luscher_quick.result_title", { locale })
  block.append(el("h3", { className: "phase-title" }, title))

  const tags = el("div", { className: "interpretation-blocks__tags" })
  if (view.status_label) tags.append(el("span", { className: "tag" }, view.status_label))
  for (const tag of view.tags || []) tags.append(el("span", { className: "tag" }, tag))
  if (tags.childNodes.length) block.append(tags)

  if (view.warnings?.length) {
    const ul = el("ul", { className: "report-list interpretation-blocks__warnings" })
    for (const w of view.warnings) ul.append(el("li", {}, w))
    block.append(ul)
  }

  if (view.classic_phase1) {
    if (view.classic_numeric_title) {
      block.append(el("h4", { className: "interpretation-blocks__heading" }, view.classic_numeric_title))
    }
    if (view.classic_numeric_note) block.append(el("p", { className: "hint" }, view.classic_numeric_note))
    block.append(el("p", {}, el("strong", {}, "Первая выборка: "), view.classic_phase1))
    if (view.classic_phase2) {
      block.append(el("p", {}, el("strong", {}, "Вторая выборка: "), view.classic_phase2))
    }
  }

  if (view.position_roles?.length) {
    if (view.position_roles_title) {
      block.append(el("h4", { className: "interpretation-blocks__heading" }, view.position_roles_title))
    }
    const ol = el("ol", { className: "report-list" })
    for (const role of view.position_roles) ol.append(el("li", {}, role))
    block.append(ol)
  }

  if (view.compensation_title || view.compensation_body) {
    const p = el("p")
    if (view.compensation_title) p.append(el("strong", {}, view.compensation_title), document.createTextNode(" — "))
    if (view.compensation_body) p.append(document.createTextNode(view.compensation_body))
    block.append(p)
    if (view.compensation_extra) block.append(el("p", { className: "hint" }, view.compensation_extra))
  }

  if (view.pairs?.length) {
    if (view.pairs_title) block.append(el("h4", { className: "interpretation-blocks__heading" }, view.pairs_title))
    for (const pair of view.pairs) {
      block.append(
        el("p", {}, pair.phase ? el("span", { className: "muted" }, pair.phase) : null, pair.phase ? el("br") : null, pair.text || "")
      )
    }
  }

  if (view.temperament) block.append(el("p", {}, el("strong", {}, view.temperament)))
  if (view.needs?.length) {
    const ul = el("ul", { className: "report-list" })
    for (const n of view.needs) ul.append(el("li", {}, n))
    block.append(ul)
  }
  if (view.recommendations?.length) {
    if (view.recommendations_title) {
      block.append(el("h4", { className: "interpretation-blocks__heading" }, view.recommendations_title))
    }
    const ul = el("ul", { className: "report-list" })
    for (const r of view.recommendations) ul.append(el("li", {}, r))
    block.append(ul)
  }
  if (view.disclaimer) {
    block.append(el("p", { className: "hint interpretation-blocks__disclaimer" }, view.disclaimer))
  }

  const hasContent =
    view.has_content ||
    view.status_label ||
    (view.tags || []).length ||
    view.classic_phase1 ||
    (view.position_roles || []).length ||
    view.compensation_body ||
    (view.pairs || []).length ||
    view.temperament ||
    (view.needs || []).length ||
    (view.recommendations || []).length
  return hasContent ? block : null
}

export function renderSwatchOrder(section, { locale }) {
  const data = sectionData(section)
  const root = el("div", { className: "luscher-result content" })
  let has = false

  const phases = []
  if (data.phase1_rows?.length) {
    phases.push({
      title: firstNonEmpty(data.phase1_title, t("tests.luscher_quick.phase1_title", { locale })),
      rows: mapSwatchRows(data.phase1_rows),
    })
  }
  if (data.phase2_rows?.length) {
    phases.push({
      title: firstNonEmpty(data.phase2_title, t("tests.luscher_quick.phase2_title", { locale })),
      rows: mapSwatchRows(data.phase2_rows),
    })
  }
  if (!phases.length && data.items?.length) {
    phases.push({
      title: "Порядок",
      rows: data.items.map((it, i) => ({
        position: i + 1,
        id: it.id,
        hex: it.hex || it.color,
        label: firstNonEmpty(it.label, it.id),
        onDark: false,
        interpretation: "",
      })),
    })
  }

  for (const [idx, phase] of phases.entries()) {
    has = true
    const phaseEl = el("div", { className: "luscher-result__phase" })
    phaseEl.append(el("h3", { className: "phase-title" }, phase.title))
    const strip = el("div", { className: "luscher-result__strip", role: "img", "aria-label": phase.title })
    for (const row of phase.rows) {
      strip.append(
        el(
          "div",
          {
            className: row.onDark ? "luscher-result__cell luscher-result__cell--on-dark" : "luscher-result__cell",
            style: row.hex ? `background-color:${row.hex}` : undefined,
          },
          el("span", { className: "luscher-result__rank" }, String(row.position))
        )
      )
    }
    phaseEl.append(strip)

    const details = el("details", { className: "luscher-result__details" })
    details.append(
      el(
        "summary",
        {},
        firstNonEmpty(data.interpretations_toggle_more, t("tests.luscher_quick.interpretations_toggle_more", { locale }))
      )
    )
    const cards = el("div", { className: "luscher-result__interpretations" })
    for (const row of phase.rows) {
      if (!row.interpretation) continue
      cards.append(
        el(
          "div",
          { className: "luscher-result__card" },
          el("p", { className: "luscher-result__card-meta" }, `${row.position}. ${row.label}`),
          el("p", { className: "luscher-result__card-body" }, row.interpretation)
        )
      )
    }
    if (cards.childNodes.length) {
      details.append(cards)
      phaseEl.append(details)
    }
    root.append(phaseEl)
  }

  if (data.shifts?.length) {
    has = true
    root.append(
      el(
        "h3",
        { className: "phase-title" },
        firstNonEmpty(data.shifts_title, t("tests.luscher_quick.shifts_title", { locale }))
      )
    )
    const shifts = el("div", { className: "luscher-result__shifts" })
    for (const s of data.shifts) {
      const label = firstNonEmpty(s.color_label, s.color_code)
      const line =
        s.line ||
        t("tests.luscher_quick.shift_line", {
          locale,
          color: label,
          from: s.position_phase1,
          to: s.position_phase2,
          delta: s.delta > 0 ? `+${s.delta}` : s.delta,
        })
      shifts.append(el("span", { className: "luscher-shift-tag" }, line))
    }
    root.append(shifts)
  }

  const interp = renderInterpBlock(data.interpretation_view, { locale })
  if (interp) {
    has = true
    root.append(interp)
  }

  return has ? root : rawFallback(data)
}

export function renderMarkdownBlocks(section, { locale }) {
  const data = sectionData(section)
  const root = el("div")
  let has = false

  for (const b of data.blocks || []) {
    const body = firstNonEmpty(b.body, b.text)
    if (!b.title && !body) continue
    has = true
    const box = el("div", { className: "md-block content" })
    if (b.title) box.append(el("h3", {}, b.title))
    const md = renderMarkdownHtml(body, { className: "md-block__body" })
    if (md) box.append(md)
    root.append(box)
  }
  if (data.title || data.body) {
    has = true
    const box = el("div", { className: "md-block content" })
    if (data.title) box.append(el("h3", {}, data.title))
    const md = renderMarkdownHtml(data.body, { className: "md-block__body" })
    if (md) box.append(md)
    root.append(box)
  }

  if (data.battery_summary?.length) {
    has = true
    const table = el("table", { className: "report-table" })
    table.append(el("thead", {}, el("tr", {}, el("th", {}, "Таблица"), el("th", {}, "Результат"))))
    const tbody = el("tbody")
    for (const row of data.battery_summary) {
      tbody.append(
        el("tr", {}, el("td", {}, firstNonEmpty(row.label, row.key)), el("td", {}, stringify(row.value)))
      )
    }
    table.append(tbody)
    const summary = firstNonEmpty(
      data.battery_title,
      t("tests.result.answer_protocol_title", { locale })
    )
    root.append(collapsedDetails(summary, table))
  }

  const interp = renderInterpBlock(data.interpretation_view, { locale })
  if (interp) {
    has = true
    root.append(interp)
  }

  // Full Lüscher may only carry engine interpretation without interpretation_view.
  if (!has && data.interpretation) {
    root.append(el("p", { className: "hint" }, stringify(data.interpretation.status || "")))
    has = true
  }

  return has ? root : rawFallback(data)
}

export function renderInvestigationsHistory(section, { locale }) {
  const data = sectionData(section)
  const loaded = (data.cells || []).filter((c) => c.state === "loaded" && c.key).map((c) => c.key)
  const procedure = firstNonEmpty(
    t(`szondi.procedure.${data.procedure_key}`, { locale }),
    data.procedure_key
  )
  const contrast = t(`szondi.profile_contrast.${data.contrast_key}`, { locale })
  const typology = t(`szondi.typology.${data.typology_key}`, { locale })
  if (!procedure && !contrast && !typology && !loaded.length) return rawFallback(data)

  const dl = el("dl", { className: "szondi-summary" })
  const add = (label, value) => {
    if (!value) return
    dl.append(el("div", {}, el("dt", {}, label), el("dd", {}, value)))
  }
  add(t("unified_report.investigations_history.procedure", { locale }), procedure)
  add(t("unified_report.investigations_history.loaded_vectors", { locale }), loaded.join(", "))
  add(t("unified_report.investigations_history.contrast", { locale }), contrast)
  add(t("unified_report.investigations_history.typology", { locale }), typology)
  return dl
}

function collapsedDetails(summaryText, node, className = "report-details") {
  const details = el("details", { className })
  details.append(el("summary", {}, summaryText))
  details.append(node)
  return details
}

function protocolImage(key, alt, opts, className) {
  if (!key) return null
  const src = (opts.imageUrls || {})[key]
  if (!src) return null
  return el("img", { className, src, alt: stringify(alt), loading: "lazy" })
}

export function renderAnswerProtocol(section, opts = {}) {
  const locale = opts.locale || "ru"
  const data = sectionData(section)
  const rows = Array.isArray(data.rows) ? data.rows : []
  if (!rows.length) return null
  const title = firstNonEmpty(
    t(section?.title_key, { locale }),
    section?.title,
    t("tests.result.answer_protocol_title", { locale })
  )
  const showScales = rows.some((row) => row?.scales)
  const head = el(
    "tr",
    {},
    el("th", {}, t("tests.result.answer_protocol_number", { locale })),
    el("th", {}, t("tests.result.answer_protocol_question", { locale })),
    el("th", {}, t("tests.result.answer_protocol_answer", { locale }))
  )
  if (showScales) head.append(el("th", {}, t("tests.result.answer_protocol_scales", { locale })))
  const table = el("table", { className: "report-table report-table--compact answer-protocol" })
  table.append(el("thead", {}, head))
  const tbody = el("tbody")
  for (const row of rows) {
    const prompt = el("td", {}, stringify(row?.prompt))
    const stimulus = protocolImage(row?.stimulus_image, row?.prompt, opts, "answer-protocol__stimulus")
    if (stimulus) prompt.append(stimulus)
    const answer = el("td", {}, stringify(row?.answer))
    const choice = protocolImage(row?.choice_image, row?.answer, opts, "answer-protocol__choice")
    if (choice) answer.append(choice)
    const tr = el("tr", {}, el("td", {}, stringify(row?.n)), prompt, answer)
    if (showScales) tr.append(el("td", {}, stringify(row?.scales)))
    tbody.append(tr)
  }
  table.append(tbody)
  return collapsedDetails(title, el("div", { className: "table-scroll" }, table), "report-details answer-protocol-details")
}

const SECTION_RENDERERS = {
  scale_analysis: renderScaleAnalysis,
  combination_rules: renderCombinationRules,
  contradictions: renderContradictions,
  notification: renderNotification,
  interpretation_list: renderInterpretationList,
  validity: renderValidity,
  profile_chart: renderProfileChart,
  swatch_order: renderSwatchOrder,
  markdown_blocks: renderMarkdownBlocks,
  composite_blocks: renderCompositeBlocks,
  investigations_history: renderInvestigationsHistory,
  answer_protocol: renderAnswerProtocol,
}

export function sectionTitle(section, locale = "ru") {
  if (section?.title) return section.title
  if (section?.title_key) {
    const translated = titleForKey(section.title_key, locale)
    if (translated) return translated
  }
  if (section?.type === "investigations_history") {
    return t("unified_report.investigations_history.title", { locale })
  }
  return section?.key || section?.type || ""
}

export function renderSection(section, opts = {}) {
  const locale = opts.locale || "ru"
  const type = section?.type
  if (type === "answer_protocol") return renderAnswerProtocol(section, { ...opts, locale })
  const renderer = SECTION_RENDERERS[type]
  const article = el("article", { className: "report-section panel", role: "region" })
  const title = sectionTitle(section, locale)
  if (title) article.append(el("h2", { className: "report-section__title" }, title))
  try {
    const body = renderer ? renderer(section, { ...opts, locale }) : rawFallback(section?.data)
    article.append(body || rawFallback(section?.data))
  } catch {
    article.append(rawFallback(section?.data))
  }
  return article
}

export { SECTION_RENDERERS }
